import React from "react";
import { IconCheck } from "~misc/icons";


const barThemes = {
  blue: {
    barGradient: "linear-gradient(to right, #00034D, #0E6CD5)",
    thumbColor:  "#01054C",
    color:       "#01054C",
    borderColor: "#0E6CD5",
  },
  green: {
    barGradient: "linear-gradient(to right, #205E00, #97E738)",
    thumbColor:  "#389807",
    color:       "#389807",
    borderColor: "#97E738",
  },
};

const corCard = { blue: "#01054C", green: "#389807" };

const alturaTrack   = 20;
const alturaContainer = alturaTrack + 10;
const tamanhoThumb  = 25;
const tamanhoIcone  = 44;
const alturaLinha   = Math.max(tamanhoThumb, alturaContainer);

const shimmerCss = `
  @keyframes shimmer {
    from { transform: translateX(-100%); }
    to   { transform: translateX(100%);  }
  }
  @keyframes pulseGlow {
    0%, 100% { transform: translateX(-50%) translateY(-50%) scale(1);   opacity: 1; }
    50%       { transform: translateX(-50%) translateY(-50%) scale(1.12); opacity: 0.85; }
  }
`;



const ProgressBar = ({ label, currentValue, percentage, targetValue, color = "blue" }) => {
  const t          = barThemes[color] ?? barThemes.blue;
  const pct        = Math.min(100, Math.max(0, percentage ?? 0));
  const metaBatida = pct >= 100;
  const noFim      = pct >= 96;

  const thumbLeft = noFim
    ? `calc(100% - ${tamanhoThumb / 2}px)`
    : `clamp(${tamanhoThumb / 2}px, ${pct}%, calc(100% - ${tamanhoThumb / 2}px))`;

  const cor   = metaBatida ? t.borderColor : t.color;
  const posX  = thumbLeft;
  const align = pct <= 4 && !noFim ? "0%" : "-50%";

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-semibold" style={{ color: t.color }}>
        {label}
      </span>

      <div className="relative">

        <div className="relative" style={{ height: alturaLinha }}>

          <div
            className="absolute inset-x-0 top-1/2 -translate-y-1/2 overflow-visible rounded-full p-[5px]"
            style={{
              height: alturaContainer,
              border: `1.5px solid ${t.borderColor}`,
              ...(metaBatida && { boxShadow: `0 0 10px 3px ${t.borderColor}55` }),
            }}
          >
            <div className="h-full w-full rounded-full">

              <div
                className="relative h-full overflow-hidden rounded-full"
                style={{ width: `${pct}%`, background: t.barGradient }}
              >
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%)",
                    animation: "shimmer 5s linear infinite",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Thumb */}
          <div
            className="absolute top-1/2 z-0 -translate-x-1/2 -translate-y-1/2 rounded-full shadow-md"
            style={{
              width: tamanhoThumb,
              height: tamanhoThumb,
              left: thumbLeft,
              backgroundColor: t.thumbColor,
            }}
          />


          <div
            className="absolute left-full top-1/2 z-30 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full"
            style={{
              width: tamanhoIcone,
              height: tamanhoIcone,
              backgroundColor: metaBatida ? t.borderColor : "#9DA2B6",
              ...(metaBatida && { animation: "pulseGlow 1.5s ease-in-out infinite" }),
            }}
          >
            <div style={{ transform: "scale(0.82)", lineHeight: 0 }}>
              <IconCheck />
            </div>
          </div>
        </div>


        <div className="relative h-7">
          <div
            className="absolute top-0 w-px"
            style={{ height: 10, left: posX, transform: "translateX(-50%)", backgroundColor: cor }}
          />
          <span
            className="absolute top-[10px] whitespace-nowrap text-xs font-bold"
            style={{ left: posX, transform: `translateX(${align})`, color: cor }}
          >
            {currentValue} ({pct}%)
          </span>
          {!metaBatida && (
            <span className="absolute left-full top-[10px] -translate-x-1/2 whitespace-nowrap text-xs font-medium text-gray-400">
              {targetValue}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};



const botoesPeriodo = [
  { key: "current",  label: "Mês Atual" },
  { key: "previous", label: "Mês Anterior" },
];

const CampaignProgressCard = ({
  title = "Programa",
  period = "",
  activePeriod = "current",
  onPeriodChange,
  statCards = [],
  progressItems = [],
  emptyMessage = null,
  children,
}) => (
  <div className="mx-auto w-full max-w-[1200px] rounded-2xl bg-white p-4 pr-12">
    <style>{shimmerCss}</style>

    <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
      <h2 className="text-2xl font-bold text-primaria">
        {title}
        {period && <span className="text-secundaria"> {period}:</span>}
      </h2>

      <div className="flex gap-2">
        {botoesPeriodo.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => onPeriodChange?.(key)}
            className={`rounded-lg px-7 py-2 text-sm font-semibold transition-colors focus:outline-none ${
              activePeriod === key
                ? "bg-terciaria text-white"
                : "border border-gray-300 bg-white text-gray-600 hover:border-terciaria hover:text-terciaria"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>

    {children && <div className="mt-6 mb-6">{children}</div>}

    {emptyMessage && statCards.length === 0 && (
      <div className="mt-12 rounded-xl bg-[#F7F8FA] px-5 py-6 text-center text-sm font-semibold text-[#8D96A8]">
        {emptyMessage}
      </div>
    )}

      {statCards.length > 0 && (
    <div className="flex flex-col mt-12 gap-6 lg:flex-row lg:flex-wrap lg:items-start">
        <div className="flex w-full shrink-0 flex-row gap-4 lg:w-52 lg:flex-col">
          {statCards.map((card, i) => (
            <div
              key={i}
              className="flex flex-col text-center gap-1 rounded-xl px-5 py-4"
              style={{ backgroundColor: corCard[card.color] ?? corCard.blue }}
            >
              <p className="text-sm font-medium text-white">{card.label}</p>
              <p className="text-2xl font-bold text-white">{card.value}</p>
            </div>
          ))}
        </div>


      {progressItems.length > 0 && (
        <div className="flex min-w-0 flex-1 flex-col gap-8">
          {progressItems.map((item, i) => (
            <ProgressBar key={i} {...item} />
          ))}
        </div>
      )}
    </div>
     )}
  </div>
);

export default CampaignProgressCard;
